import type {
  AnalyticsDashboardContract,
  AnalyticsEventContract,
  AnalyticsRangeParams,
  DeviceAnalyticsContract,
  VisitorRecordContract,
} from '@/contracts/analytics'
import {
  ANALYTICS_EXPORT_SERVER_REDACTION_POLICY,
  type AnalyticsExportServerRequest,
  type AnalyticsExportServerSafeResponse,
  AnalyticsExportServerSafeResponseInputSchema,
} from '@/contracts/analytics-export-server'
import { createSyntheticMeta } from '@/contracts/questionnaire'
import { getMockAnalyticsDashboard } from '@/lib/mock-api/analytics'
import { DataClientRequestError, apiOrMock, buildQuery, requestApiEnvelope } from './http'

function isAnalyticsDashboard(value: unknown): value is AnalyticsDashboardContract {
  return Boolean(value && typeof value === 'object' && 'metrics' in value && 'trend' in value)
}

export async function getAnalyticsDashboard(
  params: AnalyticsRangeParams = {},
): Promise<AnalyticsDashboardContract> {
  return apiOrMock({
    label: 'getAnalyticsDashboard',
    api: async () => {
      const formId = params.formId
      if (!formId) return getMockAnalyticsDashboard(params)
      const envelope = await requestApiEnvelope<unknown>(
        `/api/forms/${formId}/analytics${buildQuery({ range: params.range })}`,
      )
      if (!isAnalyticsDashboard(envelope.data)) {
        return getMockAnalyticsDashboard(params)
      }
      return envelope.data
    },
    mock: () => getMockAnalyticsDashboard(params),
  })
}

export type AnalyticsExportJobListResult = {
  jobs: AnalyticsExportJobSafeResponse[]
}

export type AnalyticsReadResult<T> = {
  data: T[]
  meta?: Record<string, unknown>
}

export type AnalyticsVisitorActivityClient = {
  getAnalyticsDevices: (formId: string) => Promise<AnalyticsReadResult<DeviceAnalyticsContract>>
  getAnalyticsVisitors: (formId: string) => Promise<AnalyticsReadResult<VisitorRecordContract>>
  getAnalyticsEvents: (formId: string) => Promise<AnalyticsReadResult<AnalyticsEventContract>>
}

export type AnalyticsExportJobSafeResponse = AnalyticsExportServerSafeResponse['data']

export type AnalyticsExportDownloadReadiness =
  | {
      ready: true
      job: AnalyticsExportJobSafeResponse
      meta?: Record<string, unknown>
    }
  | {
      ready: false
      status: number
      code: string
      error: string
      meta?: Record<string, unknown>
    }

export type AnalyticsExportJobClient = {
  queueAnalyticsExportJob: (
    formId: string,
    request: AnalyticsExportServerRequest,
  ) => Promise<AnalyticsExportJobSafeResponse>
  listAnalyticsExportJobs: (formId: string) => Promise<AnalyticsExportJobListResult>
  getAnalyticsExportJobDownloadReadiness: (
    formId: string,
    jobId: string,
  ) => Promise<AnalyticsExportDownloadReadiness>
}

export const analyticsExportJobClient: AnalyticsExportJobClient = {
  queueAnalyticsExportJob,
  listAnalyticsExportJobs,
  getAnalyticsExportJobDownloadReadiness,
}

export const analyticsVisitorActivityClient: AnalyticsVisitorActivityClient = {
  getAnalyticsDevices,
  getAnalyticsVisitors,
  getAnalyticsEvents,
}

export async function getAnalyticsDevices(
  formId: string,
): Promise<AnalyticsReadResult<DeviceAnalyticsContract>> {
  const envelope = await requestApiEnvelope<unknown[]>(`/api/forms/${formId}/analytics/devices`)
  return {
    data: (Array.isArray(envelope.data) ? envelope.data : []).map(parseDeviceAnalytics),
    meta: envelope.meta,
  }
}

export async function getAnalyticsVisitors(
  formId: string,
): Promise<AnalyticsReadResult<VisitorRecordContract>> {
  const envelope = await requestApiEnvelope<unknown[]>(`/api/forms/${formId}/analytics/visitors`)
  return {
    data: (Array.isArray(envelope.data) ? envelope.data : []).map(parseVisitorRecord),
    meta: envelope.meta,
  }
}

export async function getAnalyticsEvents(
  formId: string,
): Promise<AnalyticsReadResult<AnalyticsEventContract>> {
  const envelope = await requestApiEnvelope<unknown[]>(`/api/forms/${formId}/analytics/events`)
  return {
    data: (Array.isArray(envelope.data) ? envelope.data : []).map(parseAnalyticsEvent),
    meta: envelope.meta,
  }
}

export async function queueAnalyticsExportJob(
  formId: string,
  request: AnalyticsExportServerRequest,
): Promise<AnalyticsExportJobSafeResponse> {
  const envelope = await requestApiEnvelope<unknown>(`/api/forms/${formId}/analytics/export-jobs`, {
    method: 'POST',
    body: JSON.stringify(request),
  })
  return parseAnalyticsExportJobSafeResponse(envelope.data)
}

export async function listAnalyticsExportJobs(
  formId: string,
): Promise<AnalyticsExportJobListResult> {
  const envelope = await requestApiEnvelope<{ jobs?: unknown[] }>(
    `/api/forms/${formId}/analytics/export-jobs`,
  )
  return {
    jobs: (envelope.data.jobs ?? []).map(parseAnalyticsExportJobSafeResponse),
  }
}

export async function getAnalyticsExportJobDownloadReadiness(
  formId: string,
  jobId: string,
): Promise<AnalyticsExportDownloadReadiness> {
  const { response, body } = await requestApiEnvelopeWithResponse<unknown>(
    `/api/forms/${formId}/analytics/export-jobs/${jobId}/download`,
  )

  if (response.ok) {
    return {
      ready: true,
      job: parseAnalyticsExportJobSafeResponse(body.data),
      meta: body.meta,
    }
  }

  if (
    (response.status === 409 && body.code === 'analytics_export_file_not_ready') ||
    (response.status === 503 &&
      body.code === 'provider_not_configured' &&
      body.meta?.r2_read === 'not_configured')
  ) {
    return {
      ready: false,
      status: response.status,
      code: body.code,
      error: body.error ?? 'Analytics export file is not ready',
      meta: body.meta,
    }
  }

  throw new DataClientRequestError(
    response.status,
    body.error ?? `API request failed: ${response.status}`,
    body.code,
    body.details,
  )
}

function parseDeviceAnalytics(value: unknown): DeviceAnalyticsContract {
  const record = asRecord(value)
  const device = parseAnalyticsDevice(record.device)
  return {
    device,
    label: readString(record.label, getAnalyticsDeviceLabel(device)),
    visitors: readNumber(record.visitors),
    completionRate: readNumber(record.completionRate),
    averageTimeSeconds: readNumber(record.averageTimeSeconds),
  }
}

function parseVisitorRecord(value: unknown): VisitorRecordContract {
  const record = asRecord(value)
  const utm = asRecord(record.utm)
  return {
    id: readString(record.id, 'visitor-safe-record'),
    visitedAt: readString(record.visitedAt, ''),
    sourceLabel: readString(record.sourceLabel, '未標示來源'),
    utm: {
      source: readString(utm.source, 'unknown'),
      medium: readString(utm.medium, 'unknown'),
      campaign: readString(utm.campaign, 'unknown'),
    },
    salesCode: readString(record.salesCode, ''),
    campaignCode: readString(record.campaignCode, ''),
    deviceLabel: readString(record.deviceLabel, '未標示裝置'),
    browser: readString(record.browser, ''),
    country: readString(record.country, ''),
    city: readString(record.city, ''),
    sessionDurationSeconds: readNumber(record.sessionDurationSeconds),
    started: Boolean(record.started),
    completed: Boolean(record.completed),
    hiddenFields: asStringRecord(record.hiddenFields),
    sourceMeta: createSyntheticMeta('2026-06-06'),
  }
}

function parseAnalyticsEvent(value: unknown): AnalyticsEventContract {
  const record = asRecord(value)
  return {
    id: readString(record.id, 'event-safe-record'),
    eventType: parseAnalyticsEventType(record.eventType),
    occurredAt: readString(record.occurredAt, ''),
    questionId: typeof record.questionId === 'string' ? record.questionId : null,
    responseLinked: Boolean(record.responseLinked),
    visitorLinked: Boolean(record.visitorLinked),
    sourceMeta: createSyntheticMeta('2026-06-06'),
  }
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : {}
}

function asStringRecord(value: unknown): Record<string, string> {
  return Object.fromEntries(
    Object.entries(asRecord(value)).filter(
      (entry): entry is [string, string] => typeof entry[1] === 'string',
    ),
  )
}

function readString(value: unknown, fallback: string) {
  return typeof value === 'string' ? value : fallback
}

function readNumber(value: unknown) {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0
}

function parseAnalyticsDevice(value: unknown): DeviceAnalyticsContract['device'] {
  if (value === 'mobile' || value === 'tablet' || value === 'desktop') return value
  return 'desktop'
}

function getAnalyticsDeviceLabel(device: DeviceAnalyticsContract['device']) {
  if (device === 'mobile') return '手機'
  if (device === 'tablet') return '平板'
  return '桌機'
}

function parseAnalyticsEventType(value: unknown): AnalyticsEventContract['eventType'] {
  if (
    value === 'form_viewed' ||
    value === 'form_started' ||
    value === 'question_viewed' ||
    value === 'question_answered' ||
    value === 'form_submitted' ||
    value === 'form_abandoned'
  ) {
    return value
  }
  return 'form_viewed'
}

function parseAnalyticsExportJobSafeResponse(value: unknown): AnalyticsExportJobSafeResponse {
  const record = value && typeof value === 'object' ? (value as Record<string, unknown>) : {}
  const input = Object.fromEntries(
    Object.entries(record).filter(([key]) => key !== 'redactionPolicy'),
  )
  const parsed = AnalyticsExportServerSafeResponseInputSchema.parse(input)
  return {
    ...parsed,
    redactionPolicy:
      record.redactionPolicy === ANALYTICS_EXPORT_SERVER_REDACTION_POLICY
        ? ANALYTICS_EXPORT_SERVER_REDACTION_POLICY
        : ANALYTICS_EXPORT_SERVER_REDACTION_POLICY,
  }
}

async function requestApiEnvelopeWithResponse<T>(
  path: string,
  init: RequestInit = {},
): Promise<{
  response: Response
  body: {
    data: T | null
    error?: string
    code?: string
    details?: unknown
    meta?: Record<string, unknown>
  }
}> {
  const headers = new Headers(init.headers)
  if (!headers.has('Content-Type') && init.body) headers.set('Content-Type', 'application/json')

  const response = await fetch(path, {
    credentials: 'include',
    ...init,
    headers,
  })
  const body = (await response.json().catch(() => null)) as {
    data: T | null
    error?: string
    code?: string
    details?: unknown
    meta?: Record<string, unknown>
  } | null

  if (!body || !('data' in body)) {
    throw new DataClientRequestError(502, 'API response is missing ApiEnvelope.data')
  }

  return { response, body }
}
